import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  BellRing,
  CalendarClock,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  LineChart,
  PieChart,
  PoundSterling,
  Save,
  Search,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import toast from 'react-hot-toast';

type ReportCategory =
  | 'Operations'
  | 'Care Quality'
  | 'Finance'
  | 'Workforce'
  | 'Compliance'
  | 'Family'
  | 'Medication'
  | 'Scheduling';

type ReportCadence = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'On demand';
type AlertSeverity = 'High' | 'Medium' | 'Low';

interface ReportTemplate {
  id: string;
  name: string;
  category: ReportCategory;
  cadence: ReportCadence;
  description: string;
  owner: string;
  formats: Array<'PDF' | 'CSV' | 'Excel'>;
  lastRun: string;
  saved: boolean;
}

interface PulseMetric {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'flat';
}

interface ChangeAlert {
  id: string;
  title: string;
  severity: AlertSeverity;
  metric: string;
  change: string;
  action: string;
}

const categories: ReportCategory[] = [
  'Operations',
  'Care Quality',
  'Finance',
  'Workforce',
  'Compliance',
  'Family',
  'Medication',
  'Scheduling',
];

const reportNames: Array<[ReportCategory, string, string]> = [
  ['Operations', 'Daily visit completion', 'Completed, missed and in-progress visits by branch and carer.'],
  ['Operations', 'Client service utilisation', 'Hours delivered, planned hours and variance by client.'],
  ['Operations', 'Open operational exceptions', 'Late visits, unassigned visits and unresolved exceptions.'],
  ['Operations', 'Care continuity score', 'Continuity by client, preferred carer and rota changes.'],
  ['Operations', 'Visit duration variance', 'Planned versus actual visit duration.'],
  ['Operations', 'Missed visit root causes', 'Missed visit reason trend and corrective actions.'],
  ['Operations', 'Incident response performance', 'Incident category, severity and close-out timing.'],
  ['Care Quality', 'Care plan review compliance', 'Reviews due, overdue and completed.'],
  ['Care Quality', 'Outcome goal progress', 'Care plan goal progress and achieved outcomes.'],
  ['Care Quality', 'Client risk changes', 'Changes in falls, nutrition, hydration and medication risk.'],
  ['Care Quality', 'QA audit scorecard', 'Audit results and findings by domain.'],
  ['Care Quality', 'Corrective action tracker', 'Open, overdue and completed corrective actions.'],
  ['Care Quality', 'Safeguarding concern log', 'Concerns, escalation status and outcomes.'],
  ['Finance', 'Profit and loss summary', 'Revenue, payroll, operating costs and margin.'],
  ['Finance', 'Revenue by service type', 'Revenue split by personal care, companionship and medication support.'],
  ['Finance', 'Invoice ageing', 'Open invoices by due date and payer.'],
  ['Finance', 'Payroll cost trend', 'Payroll cost, overtime and mileage trend.'],
  ['Finance', 'Client profitability', 'Margin and care hours by client.'],
  ['Finance', 'Unbilled visits', 'Visits completed but not yet invoiced.'],
  ['Finance', 'Cash collection forecast', 'Expected receipts by payer and date.'],
  ['Workforce', 'Carer utilisation', 'Contracted versus delivered hours.'],
  ['Workforce', 'Carer compliance matrix', 'Training, DBS, references and competencies.'],
  ['Workforce', 'Absence and sickness trend', 'Absence reason and availability impact.'],
  ['Workforce', 'Recruitment pipeline', 'Applicants, interviews, offers and start dates.'],
  ['Workforce', 'Training expiry forecast', 'Training expiring within 30, 60 and 90 days.'],
  ['Workforce', 'Supervision and appraisal tracker', 'Supervision schedule and overdue records.'],
  ['Compliance', 'CQC evidence register', 'Evidence mapped to Safe, Effective, Caring, Responsive and Well-led.'],
  ['Compliance', 'PIR readiness summary', 'PIR sections, evidence gaps and manager actions.'],
  ['Compliance', 'Complaints Regulation 16 log', 'Complaints, response due dates and learning actions.'],
  ['Compliance', 'Audit trail export', 'System events, user actions and evidence changes.'],
  ['Compliance', 'Statutory notification tracker', 'CQC notification events and submission state.'],
  ['Compliance', 'Policy review schedule', 'Policies due, owner and approval status.'],
  ['Family', 'Family engagement summary', 'Messages, updates, unread items and family portal activity.'],
  ['Family', 'Family feedback report', 'Feedback themes, sentiment and response time.'],
  ['Family', 'Shared care notes', 'Published notes and health updates by client.'],
  ['Family', 'Family notification delivery', 'Delivered, read and failed notifications.'],
  ['Family', 'Family access review', 'Portal users, access level and last login.'],
  ['Medication', 'eMAR administration summary', 'Given, refused, held and missed medicine records.'],
  ['Medication', 'PRN usage trend', 'PRN reason, pain score and follow-up outcomes.'],
  ['Medication', 'Medication safety alerts', 'Warnings, overdue medicines and missed doses.'],
  ['Medication', 'Stock and refill forecast', 'Refills due and low stock indicators.'],
  ['Medication', 'Medication competency evidence', 'Staff competency and medication training matrix.'],
  ['Scheduling', 'Rota fulfilment', 'Planned visits, allocated visits and open shifts.'],
  ['Scheduling', 'Late check-in trend', 'Late check-ins by carer, client and branch.'],
  ['Scheduling', 'Travel time efficiency', 'Travel time, mileage and route exceptions.'],
  ['Scheduling', 'Open shift ageing', 'Unfilled shifts by age and priority.'],
  ['Scheduling', 'Preferred carer matching', 'Match success against client preference.'],
  ['Scheduling', 'Weekend coverage forecast', 'Capacity forecast for weekend and on-call support.'],
  ['Operations', 'On-call activity report', 'Emergency calls, response times and outcomes.'],
  ['Finance', 'Budget versus actual', 'Monthly budget, actual cost and variance.'],
  ['Care Quality', 'Hospital admission watchlist', 'Clients at risk of admission and contributing factors.'],
  ['Compliance', 'Information governance evidence', 'DPIA, DSPT, retention and access review evidence.'],
];

const reportTemplates: ReportTemplate[] = reportNames.map(([category, name, description], index) => ({
  id: `report-template-${index + 1}`,
  name,
  category,
  cadence: index % 5 === 0 ? 'Daily' : index % 5 === 1 ? 'Weekly' : index % 5 === 2 ? 'Monthly' : index % 5 === 3 ? 'Quarterly' : 'On demand',
  description,
  owner: category === 'Finance' ? 'Finance manager' : category === 'Compliance' ? 'Registered manager' : category === 'Workforce' ? 'HR lead' : 'Operations lead',
  formats: index % 3 === 0 ? ['PDF', 'CSV', 'Excel'] : index % 3 === 1 ? ['PDF', 'Excel'] : ['CSV', 'Excel'],
  lastRun: new Date(Date.now() - (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
  saved: index < 8,
}));

const pulseMetrics: PulseMetric[] = [
  { label: 'Visit completion', value: '96.8%', change: '+1.2% vs last week', trend: 'up' },
  { label: 'Gross margin', value: '34.6%', change: '+2.4% vs last month', trend: 'up' },
  { label: 'Open alerts', value: '7', change: '-3 since yesterday', trend: 'down' },
  { label: 'Carer utilisation', value: '82%', change: 'flat', trend: 'flat' },
  { label: 'Invoice collection', value: '91%', change: '+5.1% vs last month', trend: 'up' },
  { label: 'CQC evidence gaps', value: '3', change: '-2 this week', trend: 'down' },
];

const changeAlerts: ChangeAlert[] = [
  {
    id: 'alert-1',
    title: 'Medication missed-dose rate increased',
    severity: 'High',
    metric: 'eMAR missed records',
    change: '+2 records in 24 hours',
    action: 'Review eMAR audit and carer medication competency evidence.',
  },
  {
    id: 'alert-2',
    title: 'Payroll cost above forecast',
    severity: 'Medium',
    metric: 'Payroll variance',
    change: '+7.8% against budget',
    action: 'Check overtime, mileage and open shift coverage.',
  },
  {
    id: 'alert-3',
    title: 'Family portal engagement improved',
    severity: 'Low',
    metric: 'Family messages read',
    change: '+18% this week',
    action: 'Use family feedback report for care review evidence.',
  },
];

const profitLossRows = [
  { label: 'Care revenue', actual: 58240, budget: 56000 },
  { label: 'Payroll cost', actual: -32480, budget: -31500 },
  { label: 'Travel and mileage', actual: -4120, budget: -3900 },
  { label: 'Training and recruitment', actual: -2150, budget: -2500 },
  { label: 'Admin and systems', actual: -6950, budget: -6800 },
];

const severityStyles: Record<AlertSeverity, string> = {
  High: 'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low: 'bg-blue-100 text-blue-800',
};

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<'pulse' | 'catalog' | 'profit-loss' | 'alerts' | 'saved'>('pulse');
  const [category, setCategory] = useState<'All' | ReportCategory>('All');
  const [search, setSearch] = useState('');
  const [savedReports, setSavedReports] = useState(reportTemplates.filter((report) => report.saved));

  const filteredReports = useMemo(() => {
    return reportTemplates.filter((report) => {
      const matchesCategory = category === 'All' || report.category === category;
      const haystack = `${report.name} ${report.description} ${report.owner}`.toLowerCase();
      return matchesCategory && haystack.includes(search.toLowerCase());
    });
  }, [category, search]);

  const profit = profitLossRows.reduce((total, row) => total + row.actual, 0);
  const budgetProfit = profitLossRows.reduce((total, row) => total + row.budget, 0);
  const margin = Math.round((profit / profitLossRows[0]!.actual) * 1000) / 10;

  const saveReport = (report: ReportTemplate) => {
    if (savedReports.some((item) => item.id === report.id)) {
      toast.error('Report already saved');
      return;
    }
    setSavedReports((items) => [report, ...items]);
    toast.success('Report saved');
  };

  const downloadReport = (report: ReportTemplate, format: 'json' | 'csv' = 'json') => {
    const payload = {
      generatedAt: new Date().toISOString(),
      source: 'Fuchsia Home Care local reporting mode',
      report,
      note: 'Connect this template to the production database for live report data.',
    };
    const isCsv = format === 'csv';
    const content = isCsv
      ? `Report,Category,Cadence,Owner,Last Run\n"${report.name}","${report.category}","${report.cadence}","${report.owner}","${report.lastRun}"\n`
      : JSON.stringify(payload, null, 2);
    const blob = new Blob([content], { type: isCsv ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.${isCsv ? 'csv' : 'json'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${report.name} exported`);
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <BarChart3 className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Reporting
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">Pulse Reports Centre</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Monitor operational pulse, profit/loss, report templates, saved reports and alerts
                on meaningful changes. Data is local until production database reporting is connected.
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <span className="font-semibold text-slate-950">{reportTemplates.length}</span> report templates available
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        <MetricCard label="Templates" value={String(reportTemplates.length)} icon={<FileText className="h-5 w-5" />} />
        <MetricCard label="Saved reports" value={String(savedReports.length)} icon={<Save className="h-5 w-5" />} />
        <MetricCard label="Change alerts" value={String(changeAlerts.length)} icon={<BellRing className="h-5 w-5" />} tone="warning" />
        <MetricCard label="P/L margin" value={`${margin}%`} icon={<PoundSterling className="h-5 w-5" />} tone="success" />
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'pulse', label: 'Pulse Dashboard' },
            { id: 'catalog', label: '50+ Reports' },
            { id: 'profit-loss', label: 'Profit/Loss' },
            { id: 'alerts', label: 'Alerts' },
            { id: 'saved', label: 'Saved Reports' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'pulse' && (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {pulseMetrics.map((metric) => (
            <article key={metric.label} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-500">{metric.label}</p>
                  <p className="mt-2 text-3xl font-bold text-slate-950">{metric.value}</p>
                  <p className="mt-1 text-sm text-slate-600">{metric.change}</p>
                </div>
                <TrendIcon trend={metric.trend} />
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'catalog' && (
        <section className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search reports by name, owner or description"
                  className="h-11 w-full rounded-md border border-slate-300 pl-10 pr-3 text-sm"
                />
              </label>
              <label className="relative block">
                <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value as 'All' | ReportCategory)}
                  className="h-11 w-full rounded-md border border-slate-300 pl-10 pr-3 text-sm"
                >
                  <option value="All">All categories</option>
                  {categories.map((item) => (
                    <option key={item} value={item}>{item}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {filteredReports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                onSave={saveReport}
                onDownload={downloadReport}
              />
            ))}
          </div>
        </section>
      )}

      {activeTab === 'profit-loss' && (
        <section className="grid gap-6 xl:grid-cols-[1fr_0.8fr]">
          <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <PoundSterling className="h-5 w-5 text-[#6c3a80]" />
                <h2 className="text-xl font-bold text-slate-950">Profit and Loss</h2>
              </div>
            </div>
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Line item</th>
                  <th className="px-4 py-3">Actual</th>
                  <th className="px-4 py-3">Budget</th>
                  <th className="px-4 py-3">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {profitLossRows.map((row) => (
                  <tr key={row.label}>
                    <td className="px-4 py-3 font-medium text-slate-950">{row.label}</td>
                    <td className="px-4 py-3 text-slate-700">{money(row.actual)}</td>
                    <td className="px-4 py-3 text-slate-700">{money(row.budget)}</td>
                    <td className={`px-4 py-3 font-semibold ${row.actual - row.budget >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {money(row.actual - row.budget)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">Financial Pulse</h2>
            <div className="mt-5 space-y-4">
              <FinancialStat label="Actual profit" value={money(profit)} />
              <FinancialStat label="Budget profit" value={money(budgetProfit)} />
              <FinancialStat label="Margin" value={`${margin}%`} />
              <FinancialStat label="Revenue" value={money(profitLossRows[0]!.actual)} />
            </div>
          </article>
        </section>
      )}

      {activeTab === 'alerts' && (
        <section className="grid gap-4 xl:grid-cols-3">
          {changeAlerts.map((alert) => (
            <article key={alert.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-bold text-slate-950">{alert.title}</h2>
                  <p className="mt-2 text-sm text-slate-600">{alert.metric}</p>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${severityStyles[alert.severity]}`}>
                  {alert.severity}
                </span>
              </div>
              <p className="mt-4 text-lg font-bold text-slate-950">{alert.change}</p>
              <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">{alert.action}</p>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'saved' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {savedReports.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              onSave={saveReport}
              onDownload={downloadReport}
              savedView
            />
          ))}
        </section>
      )}
    </div>
  );
}

function ReportCard({
  report,
  onSave,
  onDownload,
  savedView = false,
}: {
  report: ReportTemplate;
  onSave: (report: ReportTemplate) => void;
  onDownload: (report: ReportTemplate, format?: 'json' | 'csv') => void;
  savedView?: boolean;
}) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
            <FileSpreadsheet className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-bold text-slate-950">{report.name}</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">{report.description}</p>
          </div>
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
          {report.category}
        </span>
      </div>
      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
        <Detail label="Cadence" value={report.cadence} />
        <Detail label="Owner" value={report.owner} />
        <Detail label="Last run" value={formatDate(report.lastRun)} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {report.formats.map((format) => (
          <span key={format} className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
            {format}
          </span>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {!savedView && (
          <button
            type="button"
            onClick={() => onSave(report)}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Save className="h-4 w-4" />
            Save
          </button>
        )}
        <button
          type="button"
          onClick={() => onDownload(report, 'json')}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-[#6c3a80] px-3 text-sm font-semibold text-white hover:bg-[#5e3474]"
        >
          <Download className="h-4 w-4" />
          JSON
        </button>
        <button
          type="button"
          onClick={() => onDownload(report, 'csv')}
          className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Download className="h-4 w-4" />
          CSV
        </button>
      </div>
    </article>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const styles = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${styles[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
    </article>
  );
}

function TrendIcon({ trend }: { trend: PulseMetric['trend'] }) {
  if (trend === 'up') return <TrendingUp className="h-6 w-6 text-emerald-600" />;
  if (trend === 'down') return <TrendingDown className="h-6 w-6 text-red-600" />;
  return <LineChart className="h-6 w-6 text-slate-500" />;
}

function FinancialStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-950">{value}</p>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

function money(value: number) {
  const abs = Math.abs(value).toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  });
  return value < 0 ? `-${abs}` : abs;
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
